import React, { useCallback, useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { CheckCircle, Clock, Loader2, RefreshCcw, XCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  getPendingCheckIns,
  getConfirmedCheckIns,
  confirmCheckIn,
  cancelPendingCheckIn,
} from "../../lib/therapistApi";
import ClientIntakeFormView from "../ClientIntakeFormView";

const CheckInsManagement = () => {
  const [pendingCheckIns, setPendingCheckIns] = useState([]);
  const [confirmedCheckIns, setConfirmedCheckIns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("pending");
  const [intakeFormOpen, setIntakeFormOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const loadPendingCheckIns = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPendingCheckIns();
      console.log("Pending check-ins data:", data);
      setPendingCheckIns(data.bookings || []);
    } catch (error) {
      console.error("Error loading pending check-ins:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load pending check-ins",
      );
      setPendingCheckIns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadConfirmedCheckIns = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getConfirmedCheckIns();
      setConfirmedCheckIns(data.bookings || []);
    } catch (error) {
      toast.error(error?.message || "Failed to load confirmed check-ins");
      setConfirmedCheckIns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadData = useCallback(() => {
    if (activeTab === "pending") {
      loadPendingCheckIns();
    } else {
      loadConfirmedCheckIns();
    }
  }, [activeTab, loadPendingCheckIns, loadConfirmedCheckIns]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleConfirmCheckIn = async (bookingId) => {
    try {
      await confirmCheckIn(bookingId);
      toast.success("Check-in confirmed successfully");
      await loadPendingCheckIns();
      await loadConfirmedCheckIns();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to confirm check-in",
      );
    }
  };

  const handleCancelCheckIn = async (bookingId) => {
    if (!window.confirm("Are you sure you want to cancel this check-in?"))
      return;

    try {
      await cancelPendingCheckIn(bookingId);
      toast.success("Check-in cancelled successfully");
      await loadPendingCheckIns();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to cancel check-in",
      );
    }
  };

  const handleViewIntakeForm = (booking) => {
    setSelectedBooking(booking);
    setIntakeFormOpen(true);
  };

  const renderBookingCard = (booking, isPending = true) => {
    const therapistName =
      booking.therapist?.firstName && booking.therapist?.lastName
        ? `${booking.therapist.firstName} ${booking.therapist.lastName}`
        : booking.therapist?.fullName || "Therapist";

    return (
      <div
        key={booking._id || booking.id}
        className="border rounded-lg p-4 hover:border-indigo-400 transition">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-gray-900">
                {booking.clientName}
              </p>
              {isPending ? (
                <Badge variant="secondary" className="gap-1">
                  <Clock className="h-3 w-3" />
                  Pending
                </Badge>
              ) : (
                <Badge variant="default" className="gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Confirmed
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-600">{booking.clientEmail}</p>
            <p className="text-sm text-gray-600">
              Therapist: <span className="font-medium">{therapistName}</span>
            </p>
            <p className="text-sm text-gray-600">
              {format(parseISO(booking.appointmentDate), "PPP 'at' p")} (
              {booking.duration} min)
            </p>
            {booking.notes && (
              <p className="text-sm text-gray-500 italic">{booking.notes}</p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleViewIntakeForm(booking)}
              className="gap-1">
              <FileText className="h-4 w-4" />
              View Intake Form
            </Button>
            {isPending && (
              <>
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => handleConfirmCheckIn(booking._id || booking.id)}
                  className="gap-1">
                  <CheckCircle className="h-4 w-4" />
                  Confirm
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleCancelCheckIn(booking._id || booking.id)}
                  className="gap-1">
                  <XCircle className="h-4 w-4" />
                  Cancel
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Client Check-Ins
          </h1>
          <p className="text-sm text-gray-600">
            Manage client check-ins for appointments
          </p>
        </div>
        <Button
          variant="outline"
          onClick={loadData}
          disabled={loading}
          className="gap-2">
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCcw className="h-4 w-4" />
          )}
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="pending" className="gap-2">
            <Clock className="h-4 w-4" />
            Pending ({pendingCheckIns.length})
          </TabsTrigger>
          <TabsTrigger value="confirmed" className="gap-2">
            <CheckCircle className="h-4 w-4" />
            Confirmed ({confirmedCheckIns.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-500" />
                Pending Check-Ins
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex h-40 items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
                </div>
              ) : pendingCheckIns.length > 0 ? (
                <div className="space-y-3">
                  {pendingCheckIns.map((booking) =>
                    renderBookingCard(booking, true),
                  )}
                </div>
              ) : (
                <div className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-gray-500">
                  <Clock className="h-6 w-6 text-amber-400" />
                  <p>No pending check-ins at the moment.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="confirmed" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Confirmed Check-Ins
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex h-40 items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
                </div>
              ) : confirmedCheckIns.length > 0 ? (
                <div className="space-y-3">
                  {confirmedCheckIns.map((booking) =>
                    renderBookingCard(booking, false),
                  )}
                </div>
              ) : (
                <div className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-gray-500">
                  <CheckCircle className="h-6 w-6 text-green-400" />
                  <p>No confirmed check-ins yet.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Intake Form Dialog */}
      {selectedBooking && (
        <ClientIntakeFormView
          bookingId={selectedBooking._id || selectedBooking.id}
          clientEmail={selectedBooking.clientEmail}
          clientId={selectedBooking.clientId}
          open={intakeFormOpen}
          onOpenChange={setIntakeFormOpen}
        />
      )}
    </div>
  );
};

export default CheckInsManagement;
